/**
 * WARTESCHLANGE Punkt 45 — der Betriebs-Standard unter „Einstellungen → Zahlungen".
 *
 * ## Warum das hier steht und nicht in der Maske
 *
 * Es gibt zwei Masken für dieselbe Sache: den Standard des Betriebs (hier) und die Abweichung je
 * Angebot (`NachschlagenTabs.tsx`). Antoine, 17.09.2026: „Da Standard immer vorausgefüllt, soll
 * nicht stehen: Standard, sondern was der Standard ist." — und: „Standard darf auch nur die o. g.
 * Punkte sein."
 *
 * Beide Masken schreiben in dieselben Felder. Stünde die Umrechnung zweimal da, gäbe es zwei
 * Wahrheiten darüber, was „Anzahlung an" bedeutet — genau die Sorte Doppelung, aus der der Ausfall
 * vom 17.09.2026 entstand. Die WEGE und die FELD-Umrechnung kommen deshalb unverändert aus
 * `NachschlagenTabs.tsx`; hier steht nur, was am Standard wirklich anders ist: er kennt keinen
 * „wie Mandanten-Standard"-Wert, denn er IST der Standard.
 */

import { RESTZAHLUNG_STANDARD_TAGE_VOR } from "@maestro/pricing";

/** Die Teilmenge der Betriebseinstellungen, um die es hier geht. */
export interface ZahlungsStandard {
  depositMethod?: string | null;
  depositPercentBp?: number | null;
  depositAmountCents?: number | null;
  depositDueDays?: number | null;
  /** Migration 718: worauf sich depositDueDays bezieht (null = nach_bestaetigung). */
  depositDueMode?: "nach_bestaetigung" | "vor_veranstaltung" | null;
  finalPaymentMethod?: string | null;
  finalPaymentDueDaysBeforeEvent?: number | null;
  /** Punkt 116 (Migration 518): Tage NACH der Veranstaltung, bei „Rechnung nach Event". */
  finalPaymentDueDaysAfterEvent?: number | null;
  /** Punkt 116: nur noch das Zahlungsziel „N Tage nach Rechnungserhalt" (LexOffice). */
  paymentTermDays?: number | null;
  paymentPathway?: string | null;
}

/**
 * Ist am Standard eine Anzahlung vereinbart?
 *
 * Dieselbe Frage wie `hatAnzahlungsbetrag` im Modell (@maestro/pricing): eine Anzahlung ohne Höhe
 * ist keine. Die frühere Maske entschied das über das Feld „Anzahlungsart" mit dem Wert „Keine" —
 * und ließ damit „Anzahlung 100 %" neben „Anzahlungsart: Keine" zu. Über das OB entscheidet jetzt
 * der Schalter, über das WIE die Auswahl.
 */
export function standardAnzahlungAn(d: ZahlungsStandard): boolean {
  if (d.depositMethod === "none") return false;
  return (d.depositPercentBp ?? 0) > 0 || (d.depositAmountCents ?? 0) > 0;
}

/**
 * Welcher Anzahlungsweg am Standard gilt — als Wert für die Auswahl.
 *
 * `null`/nichts hinterlegt heißt **Stripe**, nicht „vor Ort". Das ist seit jeher so
 * (`faelligeZahlungNachAnnahme`, Zweig „ungesetzt = Bestandsverhalten") und an den echten Daten
 * belegt: der Hauptbetrieb hat 20 % Anzahlung ohne hinterlegten Weg, und seine Anzahlungen laufen
 * über Stripe. Die Maske muss dasselbe anzeigen, was tatsächlich passiert.
 */
export function standardAnzahlungsWeg(d: ZahlungsStandard): string {
  if (d.paymentPathway === "kostenuebernahme") return "kostenuebernahme";
  if (d.depositMethod && d.depositMethod !== "none") return d.depositMethod;
  return "stripe_immediate";
}

/**
 * Weg und Fälligkeit der Restzahlung, aus den zwei Altfeldern gelesen.
 *
 * Im Altbestand sagte EIN Feld beides zugleich („Rechnung vor Event" = Weg UND Zeitpunkt). Wer die
 * Frist ändern wollte, musste den Weg anfassen. Hier wird es wieder auseinandergenommen.
 */
export function standardRestzahlung(d: ZahlungsStandard): { weg: string; fristArt: "vor_event" | "am_event_tag" | "nach_event"; tage: number | null } {
  if (d.finalPaymentMethod === "invoice_after_event") {
    return { weg: "ueberweisung", fristArt: "nach_event", tage: d.finalPaymentDueDaysAfterEvent ?? null };
  }
  const weg = d.finalPaymentMethod === "stripe_advance" ? "stripe" : d.finalPaymentMethod === "onsite" ? "onsite" : "ueberweisung";
  // Punkt 116: leer heißt seitdem wirksam „14 Tage vorher" (die Vorgabe), nicht mehr
  // „Am Veranstaltungstag" — die Maske zeigt, was gilt.
  const tage = d.finalPaymentDueDaysBeforeEvent ?? RESTZAHLUNG_STANDARD_TAGE_VOR;
  // Null Tage davor IST der Veranstaltungstag — sonst stünde dort „0 Tage vor dem Event".
  return { weg, fristArt: tage != null && tage > 0 ? "vor_event" : "am_event_tag", tage };
}
